import type { ProtocolStep } from '../schemas/protocol.js';
import type { SessionTurn, SnapshotObjective, TurnDecision } from '../schemas/turn.js';
import { type StepCap, implicitDecision } from './implicit.js';

/**
 * The RELECTURE À L'ENTRÉE as the ENGINE reads it (issue #165, §7.2, §8.1,
 * §8.3, §8.7).
 *
 * Entering a step that carries `rereadOnEntry`, and before its first message,
 * the server calls the scribe ONCE over the last pass of the step just left,
 * with the arriving step's objectives as the grid, and writes that reading as
 * a turn of its own - `reread: true`, in the arriving step (H94). The call, the
 * row and the chaining are the server's; what is pure - whether a step
 * rereads, which exchanges the pass holds, and what the entry decides - lives
 * here, so the server, the stub and the tests read the same answer.
 */

/**
 * Does ENTERING this step reread the step just left (H93, H97)? The case
 * ticked, the step not a farewell step - `reread-on-farewell-step` refuses the
 * pair, a content posed by hand could still carry it - and at least one
 * objective to fill: « une étape sans objectif ne relit rien » (§8.3).
 */
export function rereadsOnEntry(
  step: Pick<ProtocolStep, 'farewell' | 'rereadOnEntry' | 'objectives'>,
): boolean {
  return !step.farewell && step.rereadOnEntry && step.objectives.length > 0;
}

/**
 * One EXCHANGE of the pass the reread hands the scribe (H95, H96): what the
 * interviewer wrote on a turn, and what the person answered to it. `person` is
 * never empty - a turn without an answer is no exchange.
 */
export type RereadExchange = {
  readonly interviewer: string;
  readonly person: string;
};

/**
 * The exchanges of the LAST PASS of the step just left (H95), in the order
 * they were played - what the reread reads.
 *
 * `leavingTurn` is the turn that just decided to leave the departing step; it
 * may or may not already be in `turns`, which is every turn of the session.
 * The pass is that turn and the turns of the same step before it, walked back
 * until one of three things ends it:
 *
 * - a turn of ANOTHER step - the entry of the pass;
 * - a REREAD row - the reread that opened the pass, which exchanged nothing;
 * - a turn whose decision started a new pass of the same step - the
 *   `replay-step` that ended the previous attempt (§8.4). Read as « any
 *   decision other than `continue` », which also covers a condition that goes
 *   to the step it stands on: that turn, too, ended a pass rather than
 *   belonging to this one.
 *
 * A malformed turn counts (its answer is the person's all the same); a turn
 * whose reply is null or empty does not (H95). A leaving turn that is itself a
 * reread - a step closed by its own reread - has no pass: « une étape close
 * par sa propre relecture ne relit rien » (§8.3). An empty result means NO
 * reread and NO row.
 */
export function rereadPass(
  turns: readonly SessionTurn[],
  leavingTurn: SessionTurn,
): RereadExchange[] {
  if (leavingTurn.reread) return [];
  const before = turns
    .filter((turn) => turn.index < leavingTurn.index)
    .sort((a, b) => a.index - b.index);

  const pass: SessionTurn[] = [leavingTurn];
  for (let position = before.length - 1; position >= 0; position -= 1) {
    const turn = before[position];
    if (turn === undefined) break;
    if (turn.stepId !== leavingTurn.stepId || turn.reread) break;
    if (turn.decision.outcome.kind !== 'continue') break;
    pass.unshift(turn);
  }

  const exchanges: RereadExchange[] = [];
  for (const turn of pass) {
    if (turn.reply === null || turn.reply.trim() === '') continue;
    exchanges.push({ interviewer: turn.message, person: turn.reply });
  }
  return exchanges;
}

/**
 * A cap that is NEVER reached: the reread consumes no turn of the step (§8.3),
 * so nothing in the entry decision can be decided by a ceiling.
 */
const NO_CAP: StepCap = {
  reached: false,
  turns: false,
  emptyAnswers: false,
  budget: false,
  by: null,
};

/**
 * The ENTRY DECISION of §8.3 (H97): the implicit logic played on what the
 * reread left, without a cap. Every objective settled - no mandatory missing,
 * no optional open - closes the step at once (`close-protocol` on the last
 * step, through `closingLastStep`); anything else continues, and the step
 * opens on its first message. No exit condition and no guardrail is read on
 * the reread, `producedBy` is implicit and `cap` null.
 *
 * Called only on a step that `rereadsOnEntry`: a step without objectives
 * would read as settled here and close, and it never rereads at all.
 */
export function entryDecision(input: {
  readonly step: ProtocolStep;
  readonly isLastStep: boolean;
  readonly objectives: readonly SnapshotObjective[];
}): TurnDecision {
  return implicitDecision({
    step: input.step,
    isLastStep: input.isLastStep,
    objectives: input.objectives,
    attempt: 1,
    cap: NO_CAP,
  });
}
